import { IconButton } from "@assetwatch/ui";
import { useEffect, useRef, useState } from "react";
import { stripOriginFromUrl } from "../../lib/navigationOriginKeys";

/**
 * Copies the current page URL, matching the precedent set in
 * WorkOrderDetailScreen.tsx. Shared by the asset detail side sheet
 * (IWA-19838) and the full detail page (IWA-19840) so the copied/reset
 * timeout-cleanup behavior isn't duplicated (and isn't re-broken) in two
 * places.
 */
export function CopyAssetLinkButton({
  resetKey,
  size = "sm",
}: {
  /** Resets the copied state back to default whenever this changes (e.g.
   * the asset identity) — not the asset identity string itself; the caller
   * passes `null` to skip the reset (closed state). */
  resetKey: string | number | null;
  size?: "sm" | "md";
}) {
  const [linkCopyState, setLinkCopyState] = useState<"default" | "copied">(
    "default",
  );
  const linkCopyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  useEffect(() => {
    if (resetKey !== null) {
      setLinkCopyState("default");
    }
    return () => {
      if (linkCopyResetTimeoutRef.current !== null) {
        clearTimeout(linkCopyResetTimeoutRef.current);
        linkCopyResetTimeoutRef.current = null;
      }
    };
  }, [resetKey]);

  async function copyAssetLink() {
    if (!navigator.clipboard?.writeText) return;
    try {
      // A shared link opens with the page's normal back link, not the
      // sender's cross-module one.
      await navigator.clipboard.writeText(
        stripOriginFromUrl(window.location.href),
      );
    } catch {
      return;
    }
    setLinkCopyState("copied");
    if (linkCopyResetTimeoutRef.current !== null) {
      clearTimeout(linkCopyResetTimeoutRef.current);
    }
    linkCopyResetTimeoutRef.current = setTimeout(
      () => setLinkCopyState("default"),
      1500,
    );
  }

  return (
    <IconButton
      aria-label={
        linkCopyState === "copied" ? "Asset link copied" : "Copy asset link"
      }
      /* @ds-deviation: no "link"/"chain" icon exists in the DS icon
         registry — "assign-link" is the closest available match, same
         choice already made for this pattern in WorkOrderDetailScreen.tsx. */
      icon={linkCopyState === "copied" ? "check-mark" : "assign-link"}
      onClick={() => void copyAssetLink()}
      size={size}
      variant="ghost"
    />
  );
}
